import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog } from "@mui/material";
import { AddRounded, Settings } from "@mui/icons-material";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

import { HeaderActionsContext } from "./Layout";
import SettingsDialog from "./SettingsDialog";
import AddProject from "./AddProject";
import AddClient from "./AddClient";
import { useTheme } from "../ThemeContext";
import { usePermissions } from "../permissions/PermissionsProvider";
import { ACTIONS } from "../permissions/definition";
import { createNoBackdropCloseHandler } from "../utils/dialogClose";

const CircleIconButton = ({
  icon,
  tooltipId,
  tooltipContent,
  className = "",
  disabled,
  onClick,
}) => {
  return (
    <div className="relative flex justify-center">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-tooltip-id={tooltipId}
        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-all duration-300 ${disabled ? "opacity-50 cursor-not-allowed" : "hover:scale-105"
          } ${className}`}
      >
        {icon}
      </button>
      <ReactTooltip
        id={tooltipId}
        place="bottom"
        effect="solid"
        className="tooltip-centered"
      >
        {tooltipContent}
      </ReactTooltip>
    </div>
  );
};

const HeaderActionsMinimal = () => {
  const { setHeaderActions } = useContext(HeaderActionsContext);
  const { theme } = useTheme();
  const { can } = usePermissions();
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [openAddProject, setOpenAddProject] = useState(false);
  const [openAddClient, setOpenAddClient] = useState(false);
  const addMenuRef = useRef(null);

  const canAddProject = can(ACTIONS.PROJECTS_CREATE);
  const canAddClient = can(ACTIONS.CLIENTS_EDIT);
  const canManageSettings = can(ACTIONS.SETTINGS_MANAGE_PERMISSIONS);
  const showActions = canAddProject || canAddClient || canManageSettings;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenAddProject = useCallback(() => {
    setShowAddMenu(false);
    setOpenAddProject(true);
  }, []);

  const handleOpenAddClient = useCallback(() => {
    setShowAddMenu(false);
    setOpenAddClient(true);
  }, []);

  const handleCloseAddProject = useCallback(
    () => setOpenAddProject(false),
    []
  );
  const handleCloseAddClient = useCallback(() => setOpenAddClient(false), []);

  const actionsNode = useMemo(() => {
    if (!showActions) return null;

    return (
      <div className="flex items-center gap-4">
        {(canAddProject || canAddClient) && (
          <div className="relative" ref={addMenuRef}>
            <CircleIconButton
              onClick={() => setShowAddMenu((prev) => !prev)}
              disabled={!canAddProject && !canAddClient}
              tooltipId="header-add-menu"
              tooltipContent="Add..."
              className={theme.toolbarAddButton}
              icon={<AddRounded sx={{ fontSize: 30 }} aria-hidden="true" />}
            />
            {showAddMenu && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 max-w-[calc(100vw-40px)] rounded-xl border border-gray-200 bg-white shadow-lg z-20">
                {canAddProject && (
                  <button
                    type="button"
                    onClick={handleOpenAddProject}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 ${canAddClient ? "rounded-t-xl" : "rounded-xl"
                      } hover:bg-slate-50`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-black mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 12v6m3-3h-6"
                      />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        Add Project
                      </div>
                      <div className="text-xs text-gray-500">
                        Create a new project
                      </div>
                    </div>
                  </button>
                )}

                {canAddProject && canAddClient && (
                  <div className="h-px bg-gray-100" />
                )}

                {canAddClient && (
                  <button
                    type="button"
                    onClick={handleOpenAddClient}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 ${canAddProject ? "rounded-b-xl" : "rounded-xl"
                      } hover:bg-slate-50`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-black mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
                      />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        Add Client
                      </div>
                      <div className="text-xs text-gray-500">
                        Create a new client
                      </div>
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {canManageSettings && (
          <>
            <CircleIconButton
              onClick={() => {
                setShowAddMenu(false);
                setShowSettings(true);
              }}
              tooltipId="header-settings"
              tooltipContent="Settings"
              className="bg-gray-200 text-gray-700 hover:bg-gray-300"
              icon={<Settings sx={{ fontSize: 24 }} aria-hidden="true" />}
            />
            <SettingsDialog
              open={showSettings}
              onClose={() => setShowSettings(false)}
            />
          </>
        )}

        {canAddProject && (
          <Dialog
            open={openAddProject}
            onClose={createNoBackdropCloseHandler(handleCloseAddProject)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                maxHeight: "90vh",
                overflow: "hidden",
                borderRadius: "16px",
              },
            }}
          >
            <AddProject onClose={handleCloseAddProject} />
          </Dialog>
        )}

        {canAddClient && (
          <Dialog
            open={openAddClient}
            onClose={createNoBackdropCloseHandler(handleCloseAddClient)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                maxHeight: "90vh",
                overflow: "hidden",
                borderRadius: "16px",
              },
            }}
          >
            <AddClient onClose={handleCloseAddClient} />
          </Dialog>
        )}
      </div>
    );
  }, [
    canAddClient,
    canAddProject,
    canManageSettings,
    openAddClient,
    openAddProject,
    handleCloseAddClient,
    handleCloseAddProject,
    handleOpenAddClient,
    handleOpenAddProject,
    showActions,
    showAddMenu,
    showSettings,
    theme,
  ]);

  useEffect(() => {
    setHeaderActions(actionsNode);
    return () => setHeaderActions(null);
  }, [actionsNode, setHeaderActions]);

  return null;
};

export default HeaderActionsMinimal;
